import { useState } from "react";
import { useParams } from "react-router-dom";
import { AlternativeInvoicePdfFallback, IUpdateSettingsRequest } from "shared";
import { EnvelopeIcon } from "@heroicons/react/24/outline";

import { TwoColFormRow } from "../../../Molecules/Form/TwoColFormRow";
import { OnChangeFn } from "../Main/GeneralSettings";
import { Toggle } from "../../../../../common/Atoms/Toggle";
import { InputTextAddon } from "../../../../../common/Atoms/InputTextAddon";
import { Select } from "../../../../../common/Atoms/Select";
import { Transition } from "../../../../../common/Atoms/Transition";
import { useAccountingSystemName } from '../../../../hooks/useAccountingSystemName';

import { InboundInvoiceEmailModal } from "./InboundInvoiceEmailModal";

interface AlternativeInvoicePdfProps {
  formData: IUpdateSettingsRequest;
  onChange: OnChangeFn;
}

const fallbackOptions = (accountingSystemName: string) => [
  {
    label: `Use the ${accountingSystemName} generated PDF`,
    value: AlternativeInvoicePdfFallback.UseXeroPdf,
  },
  {
    label: `Send with no PDF attached`,
    value: AlternativeInvoicePdfFallback.UseNoPdf,
  },
  {
    label: `Don't send, report an error`,
    value: AlternativeInvoicePdfFallback.ReportError,
  },
];

export function AlternativeInvoicePdf({ formData, onChange }: AlternativeInvoicePdfProps) {
  const { organisationId } = useParams();
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const accountingSystemName = useAccountingSystemName();

  function handleChange(e) {
    onChange(e.name, e.value);
  }

  return (
    <TwoColFormRow
      heading={ `Alternative Invoice PDF` }
      description={ `Use a file already attached to the invoice in ${accountingSystemName} instead of the generated invoice PDF when emailing customers.` }
    >
      <div className={ `space-y-2` }>
        <Toggle
          name={ `useAlternativeInvoicePdf` }
          checked={ formData.useAlternativeInvoicePdf || false }
          label={ `Use alternative invoice PDF` }
          onChange={ handleChange }
          description={ `Look for an invoice attachment matching a filename prefix and use it instead of the generated PDF.` }
        />

        <Transition
          speed={ `slow` }
          show={ formData.useAlternativeInvoicePdf || false }
        >
          <div className={ `space-y-2` }>
            <InputTextAddon
              name={ `alternativeInvoicePdfPrefix` }
              value={ formData.alternativeInvoicePdfPrefix || `` }
              label={ `Filename prefix` }
              onChange={ handleChange }
              description={ `Attachments whose filename contains this text will be used as the invoice PDF.` }
            />

            <Select
              name={ `alternativeInvoicePdfFallback` }
              label={ `Fallback behaviour` }
              description={ `What to do when no attachment matches the filename prefix.` }
              options={ fallbackOptions(accountingSystemName) }
              selected={ formData.alternativeInvoicePdfFallback || AlternativeInvoicePdfFallback.UseXeroPdf }
              onChange={ v => onChange(`alternativeInvoicePdfFallback`, v.value) }
            />

            <button
              type={ `button` }
              onClick={ () => setEmailModalOpen(true) }
              className={ `flex items-center text-sm text-paidniceGreen-600 hover:text-paidniceGreen-800 underline bg-transparent border-none cursor-pointer p-0` }
            >
              <EnvelopeIcon className={ `h-4 w-4 mr-1` } />
              { `Email your invoices in` }
            </button>
          </div>
        </Transition>
      </div>

      <InboundInvoiceEmailModal
        open={ emailModalOpen }
        setOpen={ setEmailModalOpen }
        organisationId={ organisationId }
      />
    </TwoColFormRow>
  );
}
